import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { getBookingsCalendar } from "@/lib/api";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

const load = (bikes, cap) => {
  if (!bikes) return "";
  const r = bikes / Math.max(cap, 1);
  if (r >= 0.8) return "bg-[#F5F5F0] text-[#001A4D] border-[#F5F5F0]";
  if (r >= 0.4) return "bg-[#2563EB] text-white border-[#2563EB]";
  return "bg-[#2563EB]/30 text-white border-[#2563EB]/40";
};

// Month grid of bike load per day, click a day to see who's booked
export const AdminCalendar = () => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const key = monthKey(cursor);

  useEffect(() => { getBookingsCalendar(key).then(setData).catch(() => setData(null)); setSelected(null); }, [key]);
  const byDate = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d.date, d])), [data]);

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const sel = selected ? byDate[selected] : null;
  const busiest = (data?.days || []).reduce((a, d) => (d.bikes > (a?.bikes || 0) ? d : a), null);

  return (
    <div className="border border-white/10 rounded-xl p-5" data-testid="bookings-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#93C5FD]" /><p className="overline text-white/60">Bike availability</p></div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
          <p data-testid="cal-month" className="font-serif text-xl w-44 text-center">{cursor.toLocaleDateString("en-NZ", { month: "long", year: "numeric" })}</p>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1">{DOW.map((d) => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dk = `${key}-${pad(day)}`;
          const d = byDate[dk];
          const isToday = dk === todayKey();
          return (
            <button
              key={dk}
              data-testid={`cal-day-${dk}`}
              onClick={() => setSelected(dk)}
              className={`relative aspect-square sm:aspect-[4/3] rounded-lg border text-left p-1 sm:p-1.5 transition-colors ${d ? load(d.bikes, data.capacity) : "border-white/10 hover:bg-white/5"} ${selected === dk ? "ring-2 ring-[#93C5FD]" : ""}`}
            >
              <span className={`text-[11px] sm:text-xs font-mono leading-none ${isToday ? "underline decoration-2" : ""}`}>{day}</span>
              {d && (
                <span className="absolute bottom-0.5 sm:bottom-1 left-1 sm:left-1.5 right-1 text-[9px] sm:text-[11px] font-mono font-semibold leading-none truncate">
                  {d.bikes}<span className="opacity-60">/{data.capacity}</span>
                  <span className="hidden md:inline opacity-70 font-normal"> bikes</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] font-mono text-white/50">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#2563EB]/30" /> quiet</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#2563EB]" /> busy</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F5F5F0]" /> nearly full</span>
        {busiest && <span className="ml-auto" data-testid="cal-busiest">Busiest: {new Date(busiest.date).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric" })} · {busiest.bikes} bikes</span>}
      </div>

      {selected && (
        <div className="mt-4 border-t border-white/10 pt-4" data-testid="cal-day-detail">
          <p className="font-serif text-lg">{new Date(selected).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" })}</p>
          {!sel ? (
            <p className="text-sm text-white/40 mt-1">No bookings — all {data?.capacity} bikes free.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(data.per_type_capacity).map(([name, cap]) => {
                  const used = sel.per_type[name] || 0;
                  return <span key={name} data-testid={`cal-type-${name.replace(/\s+/g, "-")}`} className={`px-2.5 py-1 rounded-full text-xs font-mono border ${used >= cap ? "bg-[#F5F5F0] text-[#001A4D] border-[#F5F5F0]" : used ? "border-[#93C5FD]/50 text-[#93C5FD]" : "border-white/10 text-white/40"}`}>{name} {used}/{cap}</span>;
                })}
                {sel.tours > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-mono border border-white/10 text-white/60">{sel.tours} tour code{sel.tours > 1 ? "s" : ""}</span>}
              </div>
              <ul className="mt-3 space-y-1.5">
                {sel.bookings.map((b) => (
                  <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`cal-booking-${b.id}`}>
                    <span className="font-mono text-[#93C5FD] w-14">{b.start_time || "—"}</span>
                    <span className="font-medium">{b.customer_name}</span>
                    <span className="text-white/50">{b.bikes.join(", ")} · {b.hours}h</span>
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-emerald-500/15 text-emerald-300" : "bg-[#2563EB]/20 text-[#93C5FD]"}`}>{b.status}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};
